import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { useLocalStorage } from 'usehooks-ts';
import { BackgroundJob } from 'graphql-common';
import { JOB_TRACKER_POSITION } from '@lib/enums/localStorageKeys';
import useElementDimensions from '@lib/hooks/useElementDimensions';
import DraggableContent from './DraggableContent';
import { INDENT } from './constants';
import styles from './JobTracker.module.scss';

export default function DraggableWrapper({
  jobs,
  onRemoveJobHandler,
  isAccordionOpen,
  toggleAccordion,
  onRemoveAllJobIds,
}: {
  jobs: BackgroundJob[];
  onRemoveJobHandler: (id: string) => void;
  isAccordionOpen: boolean;
  toggleAccordion: () => void;
  onRemoveAllJobIds: () => void;
}) {
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const { dimensions } = useElementDimensions(
    accordionRef,
    isAccordionOpen ? '1' : '0',
  );
  const elHeight = dimensions?.height || 40;
  const [isDragged, setIsDragged] = useState(false);
  const [dragPosition, setDragPosition] = useLocalStorage(
    JOB_TRACKER_POSITION,
    {
      x: INDENT,
      y: window.innerHeight - INDENT - elHeight,
    },
  );

  const getDefaultPosition = useCallback(
    () => ({
      x: INDENT,
      y: window.innerHeight - INDENT - (dimensions?.height || elHeight),
    }),
    [dimensions?.height, elHeight],
  );

  const adjustPosition = useCallback(
    (x: number, y: number) => {
      if (!dimensions) return dragPosition;

      const blockWidth = dimensions.width;
      const blockHeight = dimensions.height;

      let newX = x;
      let newY = y;

      // Adjust horizontal position if out of bounds
      if (newX < INDENT) newX = INDENT;
      if (newX + blockWidth > window.innerWidth - INDENT) {
        newX = window.innerWidth - INDENT - blockWidth;
      }

      // Adjust vertical position if out of bounds
      if (newY < INDENT) newY = INDENT;
      if (newY + blockHeight > window.innerHeight - INDENT) {
        newY = window.innerHeight - INDENT - blockHeight;
      }

      // If the block is still out of bounds after adjustments,
      // return to default position
      if (
        newX < 0 ||
        newY < 0 ||
        newX + blockWidth > window.innerWidth ||
        newY + blockHeight > window.innerHeight
      ) {
        return getDefaultPosition();
      }

      return { x: newX, y: newY };
    },
    [dimensions, dragPosition, getDefaultPosition],
  );

  const handleDragStart = () => {
    setIsDragged(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.delta || !dimensions) return;

    const newPosition = adjustPosition(
      dragPosition.x + event.delta.x,
      dragPosition.y + event.delta.y,
    );

    if (event.delta.x !== 0 || event.delta.y !== 0) {
      setIsDragged(true);
    }

    setDragPosition(newPosition);
    setIsDragged(false);
  };

  useEffect(() => {
    const handleResize = () => {
      // Delay the position check slightly to ensure dimensions are updated
      setTimeout(() => {
        const adjustedPosition = adjustPosition(dragPosition.x, dragPosition.y);
        if (
          adjustedPosition.x !== dragPosition.x ||
          adjustedPosition.y !== dragPosition.y
        ) {
          setDragPosition(adjustedPosition);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Initial position check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adjustPosition, dimensions, dragPosition, setDragPosition]);

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <span className={isDragged ? styles.mask : styles.maskHidden} />
      <DraggableContent
        dragPosition={dragPosition}
        accordionRef={accordionRef}
        jobs={jobs}
        onRemoveJobHandler={onRemoveJobHandler}
        isAccordionOpen={isAccordionOpen}
        toggleAccordion={toggleAccordion}
        onRemoveAllJobIds={onRemoveAllJobIds}
      />
    </DndContext>
  );
}
